import styled from 'styled-components';
import MonitoringNoContent from '../../assets/images/monitoring_no_content.png';
import AppLogoImage from '../../assets/images/repraise-logo.png';
import { Box, Button, FormControl, Link, TextField, Typography, useTheme } from '@mui/material';
import { AuthContext } from '../../providers/AuthProvider';
import React, { KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitchLogin from '../../components/Header/AvatarMenu/LanguageSwitch/LanguageSwitchLogin';
import Maintenance from '../Maintenance/Maintenance';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100vh;
`;

const LeftContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${(props) =>
    props.theme.palette.mode === 'light'
      ? props.theme.palette.divider
      : props.theme.palette.background.default};
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 500px;
`;

const StyledImage = styled.img`
  width: 50%;
`;

const Login = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { login, authenticated } = useContext(AuthContext);
  const { state } = useLocation();
  const navigate = useNavigate();

  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const userRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const validateAndLogin = async () => {
    if (userRef.current && passwordRef.current) {
      const result = await login(userRef.current.value, passwordRef.current.value, setLoading);
      if (!result) setError(true);
    }
  };

  useEffect(() => {
    document.title = `${t('app_branding')} ${t('app_home')}`;
  }, []);

  useEffect(() => {
    if (authenticated) {
      if (state && state.path) {
        navigate(state.path, { replace: true });
      } else {
        navigate('/');
      }
    }
  }, [authenticated, navigate, state]);

  return (
    <>
      <ContentContainer>
        <LeftContainer>
          <LoginContainer>
            {<StyledImage src={AppLogoImage} />}
            <Typography
              variant="h4"
              sx={{ fontWeight: '700', mt: theme.spacing(15), color: theme.palette.text.primary }}
            >
              Login
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                mt: theme.spacing(1),
                mb: theme.spacing(2),
                color: theme.palette.text.secondary,
              }}
            >
              {t('use_your_credentials')}
            </Typography>
            <FormControl
              fullWidth={true}
              sx={{ my: theme.spacing(3) }}
            >
              <TextField
                required
                label="E-Mail"
                variant="outlined"
                size="small"
                inputRef={userRef}
                error={error}
                helperText={error ? t('check_email') : null}
                onChange={(e) => {
                  setError(false);
                  if (userRef.current) {
                    userRef.current.value = e.target.value;
                  }
                }}
                sx={{
                  my: theme.spacing(2),
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  '& .MuiOutlinedInput-root:hover': {
                    '& > fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />
              <TextField
                required
                label={t('password')}
                variant="outlined"
                size="small"
                type="password"
                inputRef={passwordRef}
                error={error}
                helperText={error ? t('check_password') : null}
                onChange={(e) => {
                  setError(false);
                  if (passwordRef.current) {
                    passwordRef.current.value = e.target.value;
                  }
                }}
                onKeyDown={async (event: KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === 'Enter') {
                    await validateAndLogin();
                  }
                }}
                sx={{
                  my: theme.spacing(2),
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  '& .MuiOutlinedInput-root:hover': {
                    '& > fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />
              <Button
                disabled={loading}
                sx={{ my: theme.spacing(2) }}
                variant="contained"
                size="medium"
                onClick={async () => await validateAndLogin()}
              >
                {t('login')}
              </Button>
            </FormControl>
            <LanguageSwitchLogin sx={{ p: 0 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Link
                href="login/forgot-password"
                variant="body1"
                underline="none"
                sx={{
                  color: theme.palette.primary.main,
                  mt: theme.spacing(10),
                }}
              >
                {t('forgot_password_question')}
              </Link>
            </Box>
          </LoginContainer>
          <Typography
            variant="body2"
            sx={{ mt: theme.spacing(5), color: theme.palette.text.secondary }}
          >
            &#169; {`${t('app_owner')} ${new Date().getFullYear()}`}
          </Typography>
        </LeftContainer>
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <img
            width={500}
            src={MonitoringNoContent}
            alt="Login Teaser"
          />
        </Box>
      </ContentContainer>
    </>
  );
};

export default Login;
